class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.setupRecommendations();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling && typeof cartDrawerNote.nextElementSibling.getAttribute === 'function' && cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner && drawerInner.classList.contains('is-empty')) {
      drawerInner.classList.remove('is-empty');
    }
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      this.setupRecommendations();
      this.open();
    });
  }

  setupRecommendations() {
    const root = this.querySelector('[data-cart-recos]');
    if (!root || root.dataset.bound === 'true') return;

    const slides = Array.from(root.querySelectorAll('[data-cart-recos-slide]'));
    const dots = Array.from(root.querySelectorAll('[data-cart-recos-dot]'));
    const prev = root.querySelector('[data-cart-recos-prev]');
    const next = root.querySelector('[data-cart-recos-next]');
    const track = root.querySelector('.ferreti-cart-recos__track');
    if (!slides.length) {
      root.hidden = true;
      return;
    }

    if (slides.length < 2) {
      prev?.setAttribute('hidden', '');
      next?.setAttribute('hidden', '');
      dots.forEach((dot) => dot.setAttribute('hidden', ''));
    }

    let current = 0;
    const isTouchCarousel = () => window.matchMedia('(max-width: 749px)').matches;
    const update = (index) => {
      current = (index + slides.length) % slides.length;
      slides.forEach((slide, i) => slide.classList.toggle('is-active', i === current));
      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === current));
    };

    const goTo = (index) => {
      if (isTouchCarousel() && track) {
        const target = slides[(index + slides.length) % slides.length];
        track.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
      }
      update(index);
    };

    prev?.addEventListener('click', () => goTo(current - 1));
    next?.addEventListener('click', () => goTo(current + 1));
    dots.forEach((dot) => {
      dot.addEventListener('click', () => goTo(Number(dot.dataset.index || 0)));
    });

    track?.addEventListener(
      'scroll',
      () => {
        if (!isTouchCarousel()) return;
        const width = track.clientWidth || 1;
        update(Math.round(track.scrollLeft / width));
      },
      { passive: true }
    );

    root.querySelectorAll('[data-cart-recos-add]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.addRecommendationToCart(button);
      });
    });

    root.dataset.bound = 'true';
    update(0);
  }

  addRecommendationToCart(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.dataset.loading === 'true') return;

    const spinner = button.querySelector('.loading__spinner');
    const body = new FormData();
    body.append('id', variantId);
    body.append('quantity', '1');
    body.append(
      'sections',
      this.getSectionsToRender().map((section) => section.id)
    );
    body.append('sections_url', window.location.pathname);

    button.dataset.loading = 'true';
    button.setAttribute('aria-disabled', 'true');
    button.classList.add('loading');
    spinner?.classList.remove('hidden');
    this.setActiveElement(button);

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    config.body = body;

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          throw new Error(response.description || response.message || 'Cart add failed');
        }

        return publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-recommendations',
          productVariantId: variantId,
          cartData: response,
        }).then(() => response);
      })
      .then((response) => {
        CartPerformance.measure('add:paint-updated-sections', () => {
          this.renderContents(response);
        });
      })
      .catch((error) => {
        console.warn(error);
      })
      .finally(() => {
        button.dataset.loading = 'false';
        button.removeAttribute('aria-disabled');
        button.classList.remove('loading');
        spinner?.classList.add('hidden');
      });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector) || null;
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
